/**
 * @file    useLocalStorage.js - Vendered version of `useLocalStorage` from the
 *          `@uidotdev/usehooks` npm package.
 *
 * @author  Bryan Hoang <bryan@distributive.network>
 * @date    Aug. 2024
 *
 * @license MIT License
 *
 * Copyright (c) 2023 ui.dev
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { useCallback } from 'react';
import { useSyncExternalStore } from 'react';
import { useEffect } from 'react';

function dispatchStorageEvent(key, newValue)
{
  window.dispatchEvent(new StorageEvent('storage', { key, newValue }));
}

function setLocalStorageItem(key, value)
{
  const stringifiedValue = JSON.stringify(value);
  window.localStorage.setItem(key, stringifiedValue);
  dispatchStorageEvent(key, stringifiedValue);
}

function removeLocalStorageItem(key)
{
  window.localStorage.removeItem(key);
  dispatchStorageEvent(key, null);
}

function getLocalStorageItem(key)
{
  return window.localStorage.getItem(key);
}

function useLocalStorageSubscribe(callback)
{
  window.addEventListener('storage', callback);
  return () => window.removeEventListener('storage', callback);
}

function getLocalStorageServerSnapshot() 
{
  throw Error('useLocalStorage is a client-only hook');
}

// eslint-disable-next-line jsdoc/check-line-alignment
/**
 * Provides a convenient way to synchronize the state of a component with the
 * data stored in local storage.
 *
 * It automatically reads the initial value from local storage when the
 * component mounts and updates the local storage whenever the state changes.
 * Additionally, it listens for changes in local storage made by other tabs or
 * windows and keeps the component’s state up to date.
 *
 * @param {string} key          The key used to access the local storage value.
 * @param {any}    initialValue The initial value to use if there is no item in
 *                              the local storage with the provided key.
 * @returns {Array}
 */
export function useLocalStorage(key, initialValue)
{
  const getSnapshot = () => getLocalStorageItem(key);

  const store = useSyncExternalStore(
    useLocalStorageSubscribe,
    getSnapshot,
    getLocalStorageServerSnapshot,
  );

  const setState = useCallback(
    (v) => {
      try
      {
        const nextState = typeof v === 'function' ? v(JSON.parse(store)) : v;

        if (nextState === undefined || nextState === null) 
          removeLocalStorageItem(key);
        else
          setLocalStorageItem(key, nextState);
      }
      catch (e)
      {
        console.warn(e);
      }
    },
    [key, store],
  );

  useEffect(() => {
    if (getLocalStorageItem(key) === null && typeof initialValue !== 'undefined') 
      setLocalStorageItem(key, initialValue);
  }, [key, initialValue]);

  return [store ? JSON.parse(store) : initialValue, setState];
}
